/**
 * FederationManager — Phase 9: cross-neighborhood incident assistance.
 *
 * A "neighborhood" is a self-contained ALDS mesh. Each ALDS install has its
 * own stable NeighborhoodId (see `MeshManager.neighborhoodId`). Two
 * neighborhoods that want to assist each other on incidents create a
 * federation link by exchanging signed treaty envelopes out-of-band; once
 * linked, a dispatcher in neighborhood A can forward an incident to
 * neighborhood B with a `federation-fwd` signed envelope. B's dispatchers
 * see the incident in their env-board's "federated" tab and can either
 * assist (treating it like any local incident) or explicitly decline.
 *
 * Phase 9 scope:
 *   - link / unlink / list federation links
 *   - forwardIncident — writes the federation-fwd envelope and audits
 *   - listFederatedIncidents — reads the federated_incidents table
 *   - markDeclined — local dispatcher declines an inbound federated incident
 *
 * NOT in Phase 9 (deferred):
 *   - Inbox handling that turns inbound federation-fwd envelopes into
 *     incident_meta + federated_incidents rows on the recipient side. The
 *     piece below is the SENDER side; the RECEIVER side is Phase 9.5 work
 *     that wires into ThreadInbox.
 *   - Multi-hop federation (linkage chains)
 *   - Treaty content-validation (DSL for shared funnels, role mapping)
 *   - Selective field redaction beyond the existing day-log redactor
 */

import { v4 as uuid } from 'uuid';

import type { AppContext } from '../app';
import { signEnvelope, type SignedEnvelope } from '../envelope/Envelope';
import type {
  AuditLogRecord,
  FederationLinkRecord,
  FederationLinkStatus,
  FederationSocialFwdPayload,
  FederatedIncidentRecord,
  ForwardChainHop,
  SocialPostPayload,
  TreatyRedactionRule,
} from '../storage/schema';
import { applyRedactionRules, validateRedactionRules } from './TreatyRedactor';

/** Default hop TTL when a treaty doesn't specify one. Conservative; safe to bump per-treaty. */
export const DEFAULT_HOP_TTL = 2;
/** Hard ceiling regardless of treaty config — prevents runaway storms. */
export const MAX_HOP_TTL = 5;

export class FederationForwardError extends Error {
  constructor(message: string, public readonly reason: 'no-link' | 'paused-link' | 'ttl-exhausted' | 'cycle-detected' | 'unknown-incident' | 'invalid-rules') {
    super(message);
    this.name = 'FederationForwardError';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC TYPES
// ─────────────────────────────────────────────────────────────────────────────

export interface LinkNeighborhoodOptions {
  localNeighborhoodId: string;
  remoteNeighborhoodId: string;
  /** Out-of-band-exchanged treaty payload (canonical JSON). Hashed + recorded. */
  treaty: Record<string, unknown>;
  /** Optional ALDS funnel definitions both sides honor. */
  sharedFunnels?: string[];
  /** Per-link hop TTL for outbound forwards. Defaults to DEFAULT_HOP_TTL. */
  hopTtl?: number;
  /** Per-link treaty redaction rules. Applied to incident payload before forward. */
  redactionRules?: TreatyRedactionRule[];
  /** Whether the receiver should auto-re-forward incidents arriving on this link. */
  autoForward?: boolean;
}

export interface ForwardIncidentOptions {
  incidentId: string;
  remoteNeighborhoodId: string;
  /** Optional dispatcher note appended to the forward envelope. */
  note?: string;
  /**
   * When forwarding a previously-received federated incident, pass the
   * existing chain so the new envelope extends it. The manager validates
   * that the chain has remaining TTL and doesn't already include our
   * neighborhood (cycle).
   */
  fromChain?: ForwardChainHop[];
  /**
   * Override the per-link hop TTL for this single forward. Capped at MAX_HOP_TTL.
   */
  hopTtlOverride?: number;
}

export interface ForwardIncidentResult {
  envelopeHash: string;
  /**
   * The signed federation-fwd envelope as written. Returned so callers can
   * pipe it to outbound transports + tests can hand it to a receiver's inbox
   * without re-signing.
   */
  envelope: SignedEnvelope;
}

// ─────────────────────────────────────────────────────────────────────────────
// MANAGER
// ─────────────────────────────────────────────────────────────────────────────

export class FederationManager {
  readonly #ctx: AppContext;
  constructor(ctx: AppContext) { this.#ctx = ctx; }

  /** Idempotent — re-linking refreshes the treaty hash + status='active'. */
  async linkNeighborhood(opts: LinkNeighborhoodOptions): Promise<FederationLinkRecord> {
    const db = this.#ctx.storage.db;
    const id = `${opts.localNeighborhoodId}:${opts.remoteNeighborhoodId}`;
    const now = new Date().toISOString();

    if (opts.redactionRules !== undefined) {
      const errs = validateRedactionRules(opts.redactionRules);
      if (errs.length > 0) {
        throw new FederationForwardError(`linkNeighborhood: invalid redactionRules — ${errs.join('; ')}`, 'invalid-rules');
      }
    }
    const hopTtl = opts.hopTtl !== undefined
      ? Math.min(Math.max(1, Math.floor(opts.hopTtl)), MAX_HOP_TTL)
      : undefined;

    // Sign the treaty so we can prove agreement later. The envelope hash
    // is what we store; the canonical payload doesn't need to live here.
    const envelope = await signEnvelope({
      identity: this.#ctx.identity,
      context: { environment: 'hub', purpose: 'federation' },
      contentType: 'application/json; profile=federation-treaty',
      payload: JSON.stringify({
        kind: 'federation-treaty',
        local: opts.localNeighborhoodId,
        remote: opts.remoteNeighborhoodId,
        treaty: opts.treaty,
        ...(hopTtl !== undefined ? { hopTtl } : {}),
        ...(opts.redactionRules !== undefined ? { redactionRules: opts.redactionRules } : {}),
        ...(opts.autoForward !== undefined ? { autoForward: opts.autoForward } : {}),
        signedAt: now,
      }),
      lamport: 0,
      parentHash: '',
    });

    const existing = await db.federation_links.get(id);
    const record: FederationLinkRecord = {
      id,
      localNeighborhoodId: opts.localNeighborhoodId,
      remoteNeighborhoodId: opts.remoteNeighborhoodId,
      status: 'active',
      treatyEnvelopeHash: envelope.hash,
      ...(opts.sharedFunnels !== undefined ? { sharedFunnels: opts.sharedFunnels } : {}),
      ...(hopTtl !== undefined ? { hopTtl } : {}),
      ...(opts.redactionRules !== undefined ? { redactionRules: opts.redactionRules } : {}),
      ...(opts.autoForward !== undefined ? { autoForward: opts.autoForward } : {}),
      createdAt: existing?.createdAt ?? now,
    };
    await db.federation_links.put(record);

    await this.#audit({
      action: 'federation.linked',
      context: 'hub:federation',
      details: {
        localNeighborhoodId: opts.localNeighborhoodId,
        remoteNeighborhoodId: opts.remoteNeighborhoodId,
        treatyEnvelopeHash: envelope.hash,
        sharedFunnelCount: opts.sharedFunnels?.length ?? 0,
        hopTtl: hopTtl ?? DEFAULT_HOP_TTL,
        redactionRuleCount: opts.redactionRules?.length ?? 0,
        autoForward: opts.autoForward ?? false,
      },
    });

    return record;
  }

  /** Mark a link as paused or revoked; the row remains for audit. */
  async setLinkStatus(localNeighborhoodId: string, remoteNeighborhoodId: string, status: FederationLinkStatus): Promise<void> {
    const id = `${localNeighborhoodId}:${remoteNeighborhoodId}`;
    const db = this.#ctx.storage.db;
    const row = await db.federation_links.get(id);
    if (row === undefined) {
      throw new Error(`FederationManager.setLinkStatus: unknown link ${id}`);
    }
    const update: Partial<FederationLinkRecord> = { status };
    if (status === 'revoked') update.revokedAt = new Date().toISOString();
    await db.federation_links.update(id, update);
    await this.#audit({
      action: `federation.${status}`,
      context: 'hub:federation',
      details: { localNeighborhoodId, remoteNeighborhoodId },
    });
  }

  async listLinks(filter: { status?: FederationLinkStatus } = {}): Promise<FederationLinkRecord[]> {
    const rows = await this.#ctx.storage.db.federation_links.toArray();
    if (filter.status !== undefined) {
      return rows.filter((r) => r.status === filter.status);
    }
    return rows;
  }

  /**
   * Forward an incident to a remote neighborhood. Supports both origin-side
   * forwards (an incident in our own neighborhood) and re-forwards (a
   * federated incident we received from a peer that we're propagating along
   * to another link).
   *
   * Multi-hop checks (Phase 14):
   *   1. The inbound chain (if any) must NOT already contain our
   *      `localNeighborhoodId` (cycle).
   *   2. The chain length must be strictly less than the effective TTL
   *      (link's hopTtl, optionally overridden, capped at MAX_HOP_TTL).
   *   3. Per-treaty redaction rules are applied to the incident payload
   *      before serializing the envelope.
   */
  async forwardIncident(opts: ForwardIncidentOptions): Promise<ForwardIncidentResult> {
    const db = this.#ctx.storage.db;
    const incident = await db.incident_meta.get(opts.incidentId);
    if (incident === undefined) {
      throw new FederationForwardError(
        `FederationManager.forwardIncident: unknown incident ${opts.incidentId}`,
        'unknown-incident',
      );
    }
    // Verify there's an active link.
    const localId = await this.#computeLocalNeighborhoodId();
    const linkId = `${localId}:${opts.remoteNeighborhoodId}`;
    const link = await db.federation_links.get(linkId);
    if (link === undefined) {
      throw new FederationForwardError(
        `FederationManager.forwardIncident: no link to ${opts.remoteNeighborhoodId}`,
        'no-link',
      );
    }
    if (link.status !== 'active') {
      throw new FederationForwardError(
        `FederationManager.forwardIncident: link to ${opts.remoteNeighborhoodId} is ${link.status}`,
        'paused-link',
      );
    }

    // ─── Phase 14: multi-hop validation ───────────────────────────────────
    const inboundChain: ForwardChainHop[] = opts.fromChain ?? [];
    // Cycle: our own neighborhoodId must not appear in the inbound chain.
    if (inboundChain.some((h) => h.sourceNeighborhoodId === localId)) {
      throw new FederationForwardError(
        `FederationManager.forwardIncident: cycle — chain already contains ${localId}`,
        'cycle-detected',
      );
    }
    // Cycle (target side): the destination must not be in the inbound chain.
    if (inboundChain.some((h) => h.sourceNeighborhoodId === opts.remoteNeighborhoodId)) {
      throw new FederationForwardError(
        `FederationManager.forwardIncident: cycle — chain already contains target ${opts.remoteNeighborhoodId}`,
        'cycle-detected',
      );
    }
    const effectiveTtl = clampTtl(opts.hopTtlOverride ?? link.hopTtl ?? DEFAULT_HOP_TTL);
    // Each hop in the chain consumes one TTL; outbound creates one more.
    // Reject when adding our hop would exceed the TTL.
    if (inboundChain.length + 1 > effectiveTtl) {
      throw new FederationForwardError(
        `FederationManager.forwardIncident: hop TTL ${effectiveTtl} exhausted (chain length ${inboundChain.length})`,
        'ttl-exhausted',
      );
    }

    // Build redacted incident payload per treaty rules. Rules' field paths
    // are relative to the incident object (e.g. `narrative`, `location`).
    const baseIncident = redactForForward(incident);
    const redactedIncident = applyRedactionRules(baseIncident, link.redactionRules);

    // Build the federation-fwd envelope.
    const fwdId = uuid();
    const forwardedAt = new Date().toISOString();
    const outboundChain: ForwardChainHop[] = [
      ...inboundChain,
      // The hop we're about to mint — envelopeHash is filled in after signing.
      // We sign with a placeholder, then patch; simpler: sign once with chain
      // up to but not including the new hop, and let the receiver compute the
      // final chain as: inboundChain + {envelopeHash, source=localId, forwardedAt}.
    ];
    const envelope = await signEnvelope({
      identity: this.#ctx.identity,
      context: { environment: 'hub', purpose: 'federation' },
      contentType: 'application/json; profile=federation-fwd',
      payload: JSON.stringify({
        kind: 'federation-fwd',
        fwdId,
        sourceNeighborhoodId: localId,
        remoteNeighborhoodId: opts.remoteNeighborhoodId,
        incident: redactedIncident,
        ...(opts.note !== undefined ? { note: opts.note } : {}),
        forwardedAt,
        // Chain is everything BEFORE this hop. The receiver appends
        // {envelopeHash: this envelope's hash, source: localId, forwardedAt}.
        chain: outboundChain,
        hopTtl: effectiveTtl,
      }),
      lamport: 0,
      parentHash: '',
    });

    // For the sender, also record in federated_incidents (with our own
    // neighborhood as source) so the dispatcher can see what they sent.
    const fwdRow: FederatedIncidentRecord = {
      incidentId: opts.incidentId,
      sourceNeighborhoodId: localId,
      forwardedAt,
      envelopeHash: envelope.hash,
      ...(inboundChain.length > 0 ? {
        forwardChain: [
          ...inboundChain,
          { envelopeHash: envelope.hash, sourceNeighborhoodId: localId, forwardedAt },
        ],
      } : {}),
    };
    await db.federated_incidents.put(fwdRow);

    await this.#audit({
      action: 'federation.forwarded',
      context: incident.context,
      details: {
        incidentId: opts.incidentId,
        remoteNeighborhoodId: opts.remoteNeighborhoodId,
        envelopeHash: envelope.hash,
        fwdId,
        chainLength: inboundChain.length,
        effectiveTtl,
        redactionRulesApplied: link.redactionRules?.length ?? 0,
      },
    });

    return { envelopeHash: envelope.hash, envelope };
  }

  /**
   * Local dispatcher receives a federated incident and declines to assist.
   * Records the decision on the federated_incidents row + audits.
   */
  async markDeclined(incidentId: string): Promise<void> {
    const db = this.#ctx.storage.db;
    const row = await db.federated_incidents.get(incidentId);
    if (row === undefined) {
      throw new Error(`FederationManager.markDeclined: unknown federated incident ${incidentId}`);
    }
    await db.federated_incidents.update(incidentId, { declined: true });
    await this.#audit({
      action: 'federation.declined',
      context: 'hub:federation',
      details: {
        incidentId,
        sourceNeighborhoodId: row.sourceNeighborhoodId,
      },
    });
  }

  async listFederatedIncidents(opts: { sourceNeighborhoodId?: string; declined?: boolean } = {}): Promise<FederatedIncidentRecord[]> {
    let rows = await this.#ctx.storage.db.federated_incidents.toArray();
    if (opts.sourceNeighborhoodId !== undefined) {
      rows = rows.filter((r) => r.sourceNeighborhoodId === opts.sourceNeighborhoodId);
    }
    if (opts.declined !== undefined) {
      rows = rows.filter((r) => (r.declined ?? false) === opts.declined);
    }
    rows.sort((a, b) => b.forwardedAt.localeCompare(a.forwardedAt));
    return rows;
  }

  // ─── Phase 27.7: social-post federation ─────────────────────────────────

  /**
   * Forward a social post across an active federation link. The post is
   * read from local `message_cache` (it must already be cached locally —
   * i.e., we authored it, or we received it via the inbox loop), redacted
   * per `link.socialRedactionRules` (falling back to `link.redactionRules`),
   * wrapped in a `federation-social-fwd` envelope, and appended to a
   * per-link sync stream.
   *
   * Cycle + TTL semantics mirror `forwardIncident`. Receiver-side
   * defense-in-depth re-applies the same redaction rules in
   * `SocialGroupManager.processFederatedSocialPosts`.
   */
  async forwardSocialPost(opts: {
    groupId: string;
    messageHash: string;
    remoteNeighborhoodId: string;
    /** Optional inbound chain (when re-forwarding). Defaults to []. */
    fromChain?: ForwardChainHop[];
    /** Per-call TTL override; capped at MAX_HOP_TTL. */
    hopTtlOverride?: number;
  }): Promise<{ envelopeHash: string; streamId: string }> {
    const db = this.#ctx.storage.db;

    // Locate the source post in cache.
    const cacheRow = await db.message_cache.get(opts.messageHash);
    if (cacheRow === undefined) {
      throw new FederationForwardError(
        `forwardSocialPost: post ${opts.messageHash} not in local cache`,
        'unknown-incident',
      );
    }

    // Decrypt + parse.
    const plaintext = await this.#ctx.storage.decryptForSession(cacheRow.ciphertext);
    let payload: SocialPostPayload | null = null;
    try {
      const parsed = JSON.parse(new TextDecoder().decode(plaintext));
      if (parsed && typeof parsed === 'object' && parsed.kind === 'social-post' && parsed.groupId === opts.groupId) {
        payload = parsed as SocialPostPayload;
      }
    } catch {
      payload = null;
    }
    if (payload === null) {
      throw new FederationForwardError(
        `forwardSocialPost: ${opts.messageHash} is not a social-post payload for group ${opts.groupId}`,
        'unknown-incident',
      );
    }

    const localId = await this.#computeLocalNeighborhoodId();
    const linkId = `${localId}:${opts.remoteNeighborhoodId}`;
    const link = await db.federation_links.get(linkId);
    if (link === undefined) {
      throw new FederationForwardError(`forwardSocialPost: no link to ${opts.remoteNeighborhoodId}`, 'no-link');
    }
    if (link.status !== 'active') {
      throw new FederationForwardError(`forwardSocialPost: link to ${opts.remoteNeighborhoodId} is ${link.status}`, 'paused-link');
    }

    // Multi-hop checks (cycle + TTL).
    const inboundChain: ForwardChainHop[] = opts.fromChain ?? [];
    if (inboundChain.some((h) => h.sourceNeighborhoodId === localId)) {
      throw new FederationForwardError(`forwardSocialPost: cycle — chain already contains ${localId}`, 'cycle-detected');
    }
    if (inboundChain.some((h) => h.sourceNeighborhoodId === opts.remoteNeighborhoodId)) {
      throw new FederationForwardError(`forwardSocialPost: cycle — chain already contains target ${opts.remoteNeighborhoodId}`, 'cycle-detected');
    }
    const effectiveTtl = clampTtl(opts.hopTtlOverride ?? link.hopTtl ?? DEFAULT_HOP_TTL);
    if (inboundChain.length + 1 > effectiveTtl) {
      throw new FederationForwardError(`forwardSocialPost: hop TTL ${effectiveTtl} exhausted`, 'ttl-exhausted');
    }

    // Apply redaction rules. socialRedactionRules takes precedence;
    // otherwise fall back to incident redactionRules.
    const rules = link.socialRedactionRules ?? link.redactionRules;
    const redactedPayload = applyRedactionRules(
      payload as unknown as Record<string, unknown>,
      rules,
    ) as unknown as SocialPostPayload;

    const forwardedAt = new Date().toISOString();
    const fwdPayload: FederationSocialFwdPayload = {
      kind: 'federation-social-fwd',
      groupId: opts.groupId,
      originSenderPubkey: cacheRow.senderPubkey,
      payload: redactedPayload,
      chain: inboundChain,
      forwardedAt,
    };

    const envelope = await signEnvelope({
      identity: this.#ctx.identity,
      context: { environment: 'hub', purpose: 'federation' },
      contentType: 'application/json; profile=federation-social-fwd',
      payload: JSON.stringify(fwdPayload),
      lamport: 0,
      parentHash: '',
    });

    const streamId = `mesh-fed-social-${localId}-${opts.remoteNeighborhoodId}`;
    try {
      await this.#ctx.sync.open(streamId, { environment: 'hub', purpose: 'federation' });
    } catch {
      // stream may already be open — ignore
    }
    await this.#ctx.sync.append(streamId, envelope);

    await this.#audit({
      action: 'federation.social-forwarded',
      context: 'hub:federation',
      details: {
        groupId: opts.groupId,
        messageHash: opts.messageHash,
        remoteNeighborhoodId: opts.remoteNeighborhoodId,
        chainLength: inboundChain.length,
        redactionRuleCount: rules?.length ?? 0,
        envelopeHash: envelope.hash,
      },
    });

    return { envelopeHash: envelope.hash, streamId };
  }

  /**
   * Phase 27.7: receiver-side ingest. Pull-based: callers invoke this
   * after they've copied inbound `federation-social-fwd` envelopes from
   * the per-link stream into the local `envelopes` table (or pass an
   * explicit array via `envelopes`). Defense-in-depth: re-applies the
   * inbound link's redaction rules (in case the forwarder shipped pre-
   * redaction data on purpose or by bug). Returns the redacted payloads;
   * the caller decides whether to insert them into `message_cache` under
   * a locally-mirrored group (full mirroring is a Phase 27.7.x item).
   */
  async ingestInboundSocialFwd(opts: {
    sourceNeighborhoodId: string;
    /** Raw fwd payloads collected from inbound stream (envelope verification done by caller / sync layer). */
    payloads: FederationSocialFwdPayload[];
  }): Promise<{ ingested: Array<{ groupId: string; payload: SocialPostPayload; chain: ForwardChainHop[] }> }> {
    const db = this.#ctx.storage.db;
    const localId = await this.#computeLocalNeighborhoodId();
    const linkId = `${opts.sourceNeighborhoodId}:${localId}`;
    const link = await db.federation_links.get(linkId);
    // Receiver may not have a reciprocal link record (one-way treaty). If
    // missing, we apply no redaction beyond what arrived; the caller can
    // refuse based on policy.
    const rules = link?.socialRedactionRules ?? link?.redactionRules;

    const ingested: Array<{ groupId: string; payload: SocialPostPayload; chain: ForwardChainHop[] }> = [];
    for (const fwd of opts.payloads) {
      if (fwd.kind !== 'federation-social-fwd') continue;
      const redacted = applyRedactionRules(
        fwd.payload as unknown as Record<string, unknown>,
        rules,
      ) as unknown as SocialPostPayload;
      ingested.push({ groupId: fwd.groupId, payload: redacted, chain: fwd.chain });
    }

    if (ingested.length > 0) {
      await this.#audit({
        action: 'federation.social-ingested',
        context: 'hub:federation',
        details: {
          sourceNeighborhoodId: opts.sourceNeighborhoodId,
          count: ingested.length,
          redactionRuleCount: rules?.length ?? 0,
          hasLink: link !== undefined,
        },
      });
    }
    return { ingested };
  }

  // ─── Phase 43 (v8): federated search ─────────────────────────────────────

  /**
   * Issue a federated search query across every active outbound
   * federation link. Each link's `searchRedactionRules` is honored on
   * both sides (forwarder strips fields BEFORE sending; receiver
   * re-strips defensively before injecting into local search). Returns
   * the queryId so the caller can await + collect responses out-of-band
   * (responses arrive as `federation-search-response` envelopes on
   * `mesh-fed-search-${remoteId}-${localId}`).
   *
   * Multi-hop: each receiver decrements `ttl`; further-fanout is
   * receiver-side policy (auto-forward off by default).
   */
  async federateSearch(opts: {
    queryText: string;
    ttl?: number;
  }): Promise<{ queryId: string; sentTo: string[] }> {
    const db = this.#ctx.storage.db;
    const localId = await this.#computeLocalNeighborhoodId();
    const links = await db.federation_links.where('status').equals('active').toArray();
    const queryId = uuid();
    const issuedAt = new Date().toISOString();
    const ttl = clampTtl(opts.ttl ?? DEFAULT_HOP_TTL);

    const sentTo: string[] = [];
    for (const link of links) {
      const payload: import('../storage/schema').FederationSearchQueryPayload = {
        kind: 'federation-search-query',
        queryId,
        queryText: opts.queryText,
        initiator: localId,
        ttl,
        issuedAt,
      };
      const envelope = await signEnvelope({
        identity: this.#ctx.identity,
        context: { environment: 'hub', purpose: 'federation' },
        contentType: 'application/json; profile=federation-search-query',
        payload: JSON.stringify(payload),
        lamport: 0,
        parentHash: '',
      });
      const streamId = `mesh-fed-search-${localId}-${link.remoteNeighborhoodId}`;
      try { await this.#ctx.sync.open(streamId, { environment: 'hub', purpose: 'federation' }); }
      catch { /* may already be open */ }
      await this.#ctx.sync.append(streamId, envelope);
      sentTo.push(link.remoteNeighborhoodId);
    }

    await this.#audit({
      action: 'federation.search-issued',
      context: 'hub:federation',
      details: { queryId, ttl, linkCount: sentTo.length, queryPrefix: opts.queryText.slice(0, 32) },
    });

    return { queryId, sentTo };
  }

  /**
   * Receiver-side: when a `federation-search-query` envelope arrives,
   * run a local search (delegating to `SocialGroupManager.search`),
   * redact hits per `searchRedactionRules`, and emit a
   * `federation-search-response` envelope back on the per-link stream.
   *
   * Caller (FederationInbox-equivalent) handles envelope delivery; this
   * method just produces the response envelope.
   */
  async respondToFederationSearch(opts: {
    query: import('../storage/schema').FederationSearchQueryPayload;
    /** The neighborhood that sent us this query. */
    sourceNeighborhoodId: string;
  }): Promise<{ envelopeHash: string }> {
    const db = this.#ctx.storage.db;
    const localId = await this.#computeLocalNeighborhoodId();
    const linkId = `${opts.sourceNeighborhoodId}:${localId}`;
    const link = await db.federation_links.get(linkId);
    if (link === undefined || link.status !== 'active') {
      throw new FederationForwardError(`respondToFederationSearch: no active link from ${opts.sourceNeighborhoodId}`, 'no-link');
    }

    // Lazy-import the social search.
    const { getSocialGroupManager } = await import('../social/SocialGroupManager');
    const sgm = getSocialGroupManager(this.#ctx);
    const hits = await sgm.search({ query: opts.query.queryText, limit: 25 });

    // Apply redaction to each snippet.
    const rules = link.searchRedactionRules ?? link.socialRedactionRules ?? link.redactionRules;
    const redactedHits: import('../storage/schema').FederationSearchHit[] = hits.map((h) => {
      const obj = { snippet: h.snippet };
      const redacted = applyRedactionRules(obj as unknown as Record<string, unknown>, rules) as { snippet: string };
      return {
        groupId: h.groupId,
        messageHash: h.messageHash,
        snippet: redacted.snippet,
        matchedAt: h.matchedAt,
      };
    });

    const respondedAt = new Date().toISOString();
    const payload: import('../storage/schema').FederationSearchResponsePayload = {
      kind: 'federation-search-response',
      queryId: opts.query.queryId,
      responderNeighborhoodId: localId,
      hits: redactedHits,
      respondedAt,
    };
    const envelope = await signEnvelope({
      identity: this.#ctx.identity,
      context: { environment: 'hub', purpose: 'federation' },
      contentType: 'application/json; profile=federation-search-response',
      payload: JSON.stringify(payload),
      lamport: 0,
      parentHash: '',
    });
    const streamId = `mesh-fed-search-${localId}-${opts.sourceNeighborhoodId}`;
    try { await this.#ctx.sync.open(streamId, { environment: 'hub', purpose: 'federation' }); }
    catch { /* may already be open */ }
    await this.#ctx.sync.append(streamId, envelope);

    await this.#audit({
      action: 'federation.search-responded',
      context: 'hub:federation',
      details: {
        queryId: opts.query.queryId,
        hitCount: redactedHits.length,
        sourceNeighborhoodId: opts.sourceNeighborhoodId,
      },
    });

    return { envelopeHash: envelope.hash };
  }

  // ─── internals ──────────────────────────────────────────────────────────

  async #computeLocalNeighborhoodId(): Promise<string> {
    // We import MeshManager lazily to avoid a hard cycle (MeshManager has
    // no dep on FederationManager, but this keeps it that way).
    const { MeshManager } = await import('../mesh/MeshManager');
    const mm = new MeshManager(this.#ctx);
    return mm.neighborhoodId();
  }

  async #audit(input: { action: string; context: string; details: Record<string, unknown> }): Promise<void> {
    const occurredAt = new Date().toISOString();
    const canonical = JSON.stringify({
      category: 'federation' as const,
      action: input.action,
      context: input.context,
      details: input.details,
      occurredAt,
    });
    const signature = await this.#ctx.identity.sign(
      new TextEncoder().encode(canonical),
      { environment: 'hub', purpose: 'federation' },
    );
    const record: AuditLogRecord = {
      seq: undefined as unknown as number,
      category: 'federation',
      action: input.action,
      context: input.context,
      details: input.details,
      occurredAt,
      signature,
    };
    try { await this.#ctx.storage.db.audit_log.add(record); }
    catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`FederationManager.audit: ${err instanceof Error ? err.message : String(err)}`);
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// REDACTION FOR FORWARD
// ─────────────────────────────────────────────────────────────────────────────
//
// Forwarded incidents drop fields that wouldn't help a remote neighborhood:
// reporter pubkey (we hash it), submission id (local artifact). Geo stays
// at full precision — the remote may need it to triage proximity. Local
// dispatchers can do further redaction by editing this function or wiring
// per-treaty rules in Phase 9.5.

function clampTtl(ttl: number): number {
  if (!Number.isFinite(ttl) || ttl < 1) return DEFAULT_HOP_TTL;
  return Math.min(Math.floor(ttl), MAX_HOP_TTL);
}

function redactForForward(incident: {
  incidentId: string;
  threadId: string;
  category: string;
  proposedSeverity: string;
  confirmedSeverity?: string;
  status: string;
  narrative?: string;
  location?: unknown;
  createdAt: string;
}): Record<string, unknown> {
  return {
    incidentId: incident.incidentId,
    threadId: incident.threadId,
    category: incident.category,
    proposedSeverity: incident.proposedSeverity,
    ...(incident.confirmedSeverity !== undefined ? { confirmedSeverity: incident.confirmedSeverity } : {}),
    status: incident.status,
    ...(incident.narrative !== undefined ? { narrative: incident.narrative } : {}),
    ...(incident.location !== undefined ? { location: incident.location } : {}),
    createdAt: incident.createdAt,
  };
}
